<template>
  <div
    :class="[
      'user-avatar__wrapper',
      `user-avatar__wrapper--${size}`,
    ]"
  >
    <figure
      :class="[
        'image',
        getAvatarImageSizeClass,
        `user-avatar__container--${size}`,
        { 'user-avatar__container--faded': isFaded },
        customClass
      ]"
      @click="handleAvatarClick"
      @keyup.enter="handleAvatarClick"
    >
      <img
        v-if="user && user.avatarUrl"
        class="user-avatar__image is-rounded"
        :src="user.avatarUrl"
        alt="user avatar"
      />
      <div
        v-else
        :class="[
          'user-avatar__background',
          `user-avatar__background--${size}`
        ]"
      >
        <span v-if="userInitials" class="user-avatar__initials">{{ userInitials }}</span>
        <span v-else class="user-avatar__initials"><b-icon icon="account" /></span>
      </div>
    </figure>
    <b-icon
      v-if="activeGoal && activeGoal.goalStatus && activeGoal.goalStatus !== Types.GOAL_UNMARKED"
      :type="goalStatusIconType.iconColor"
      :icon="goalStatusIconType.iconName"
      custom-size="mdi-18px"
      :class="[
        'user-avatar__goal-status-icon',
        `user-avatar__goal-status-icon--${goalStatusIconType.iconModifierClass}`
      ]"
    />
    <div v-if="isHitRateVisible" class="user-avatar__hit-rate">
      <HitRate :activeUserId="user.id" size="small" />
    </div>
  </div>
</template>

<script>
import { getInitialsFromFirstAndLast } from '@/helpers/stringHelpers';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import * as Types from '@/constants/Types';
import HitRate from '@/components/HitRate.vue';

export default {
  name: 'UserAvatar',

  components: {
    HitRate,
  },

  props: {
    user: {
      type: Object,
      required: false,
    },
    activeGoal: {
      type: Object,
      required: false,
      default: null,
    },
    size: {
      type: String,
      required: false,
      validator(propValue) {
        // The value must match one of these strings
        /* @ TODO -- more sizes 'xsmall', 'small', */
        return ['small', 'medium', 'large'].indexOf(propValue) !== -1;
      },
      default: 'medium',
    },
    onClick: {
      type: Function,
      required: false,
      default: null,
    },
    customClass: {
      type: String,
      required: false,
      default: '',
    },
    isFaded: {
      type: Boolean,
      required: false,
      default: false,
    },
    isHitRateVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      Types,
    };
  },

  computed: {
    userInitials() {
      if (!this.user || isObjectEmpty(this.user)) return '';
      return getInitialsFromFirstAndLast(this.user.firstName, this.user.lastName);
    },
    goalStatusIconType() {
      let iconName = '';
      let iconColor = '';
      let iconModifierClass = '';

      if (this.activeGoal && this.activeGoal.goalStatus === this.Types.GOAL_HIT) {
        iconName = 'check';
        iconColor = 'is-white';
        iconModifierClass = 'is-success';
      } else if (this.activeGoal && this.activeGoal.goalStatus === this.Types.GOAL_MISS) {
        iconName = 'cancel';
        iconColor = 'is-white';
        iconModifierClass = 'is-danger';
      } else if (this.activeGoal && this.activeGoal.goalStatus === this.Types.GOAL_CHANGED) {
        iconName = 'asterisk';
        iconColor = 'is-dark';
        iconModifierClass = 'is-warning';
      }

      return { iconName, iconColor, iconModifierClass };
    },
    getAvatarImageSizeClass() {
      let avatarImageSizeClass;
      if (this.size === 'small') {
        avatarImageSizeClass = 'is-32x32';
      } else if (this.size === 'medium') {
        avatarImageSizeClass = 'is-48x48';
      } else if (this.size === 'large') {
        avatarImageSizeClass = 'is-64x64';
      }

      return avatarImageSizeClass;
    },
  },

  methods: {
    handleAvatarClick(event) {
      if (this.onClick) this.onClick(event);
    },
  },
};
</script>

<style lang="scss" scoped>
.user-avatar__wrapper {
  position: relative;
}

.user-avatar__wrapper--small {
  max-height: 38px;
}

.user-avatar__wrapper--medium {
  max-height: 54px;
}

.user-avatar__wrapper--large {
  max-height: 70px;
}

.user-avatar__container--small {
  min-width: 32px;
  margin: 3px;
}

.user-avatar__container--medium {
  min-width: 48px;
  margin: 3px;
}

.user-avatar__container--large {
  min-width: 64px;
  margin: 3px;
}

.user-avatar__container--faded {
  background-color: $white;
  opacity: 0.5;
}

.user-avatar__background {
  color: $white;
  font-weight: $weight-medium;
  background-color: lighten($primary, 45%);
  box-shadow: $avatar-image-box-shadow;
  border-radius: $radius-rounded;
}

.user-avatar__background--small {
  font-size: $size-6;
  width: 32px;
  height: 32px;
}

.user-avatar__background--medium {
  font-size: $size-5;
  width: 48px;
  height: 48px;
}

.user-avatar__background--large {
  font-size: $size-5;
  width: 64px;
  height: 64px;
}

.user-avatar__image {
  box-shadow: $avatar-image-box-shadow;
}

.user-avatar__initials {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.user-avatar__hit-rate {
  position: absolute;
  right: -8px;
  bottom: -4px;
  background: $white;
  box-shadow: $light-shadow;
  border-radius: 100px;
  padding: $global-whitespace-xsmall / 1.1;
}

.user-avatar__goal-status-icon {
  position: absolute;
  left: -8px;
  bottom: -3px;
  border-radius: $radius-rounded;
  box-shadow: $light-shadow;
  padding: 8px;

  &--is-warning {
    border: 4px solid $warning;
    background: $warning;
  }

  &--is-danger {
    border: 4px solid $danger;
    background: $danger;
  }

  &--is-success {
    border: 4px solid $success;
    background: $success;
  }
}
</style>
